import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { selectIsAuthenticated } from '../features/user/userSlice';
import {
  resetSignUp,
  selectLicenseCount,
  selectStep,
  selectSessionId,
  setStep,
  setTrackGroup,
} from '../features/signUp/signUpSlice';
import { store, useAppSelector } from '../stores/AppStore';
import {
  useGetPriceConfigQuery,
  useGetAllPricesQuery,
  useUpdateSignUpSessionMutation,
  useGetSessionStatusQuery,
} from '../services/apiSlice';

import MentorDetails from '../components/SignUp/MentorDetails';
import Payment from '../components/SignUp/Payment/Payment';
import SubscriptionConfirm from '../components/SignUp/SubscriptionConfirm';
import ThinkerDetails from '../components/SignUp/ThinkerDetails';
import TrackRecommendation from '../components/SignUp/TrackRecommendation';
import SignUpQuestionnaire from '../components/SignUp/Questionnaire';
import toast from 'react-hot-toast';
import About from './About';
import { TrackGroup } from '../types';

const SignUp = () => {
  const navigate = useNavigate();
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const step = useAppSelector(selectStep);
  const licenseCount = useAppSelector(selectLicenseCount);
  const sessionId = useAppSelector(selectSessionId);
  const [previousIndex, setPreviousIndex] = useState(1);
  const [searchParameters, setSearchParameters] = useSearchParams();
  const skipAbout = searchParameters.get('skipAbout');
  const program = searchParameters.get('program');
  const recovered = searchParameters.get('recovered');
  const [updateSignUpSession] = useUpdateSignUpSessionMutation();

  // Prefetch price data immediately when signup page loads
  // This ensures pricing is available when users reach the payment step
  useGetPriceConfigQuery(undefined, {
    // Refetch if data is older than 5 minutes to balance freshness with performance
    refetchOnMountOrArgChange: 300, // 5 minutes in seconds
  });

  useGetAllPricesQuery(undefined, {
    // Refetch if data is older than 5 minutes to balance freshness with performance
    refetchOnMountOrArgChange: 300, // 5 minutes in seconds
  });

  const handleNextStep = async () => {
    // Read step from the store to avoid React closure staleness.
    // Child components (e.g. ThinkerDetails) dispatch state updates
    // synchronously then call onNextStep, but this closure may still
    // hold the pre-dispatch value.
    const currentStep = store.getState().signUp.step;
    const nextStep = currentStep + 1;
    store.dispatch(setStep(nextStep));

    if (sessionId) {
      try {
        // Read fresh state directly from the store to avoid capturing
        // stale form data from this SignUp component's last render.
        const state = store.getState().signUp;
        const formData = {
          mentor: state.mentor,
          thinkers: state.thinkers,
          licenseCount: state.licenseCount,
          trackGroup: state.trackGroup,
          amounts: state.amounts,
        };

        await updateSignUpSession({
          sessionId,
          formData,
        }).unwrap();

        console.log('Updated sign-up session for step:', nextStep);
      } catch (error) {
        console.error('Failed to update sign-up session:', error);
        // Don't block user progress if session update fails
      }
    }
  };

  const handlePreviousStep = () => {
    store.dispatch(setStep(step - 1));
  };

  useEffect(() => {
    setTimeout(() => {
      setPreviousIndex(step);
    }, 500);
  }, [step]);

  useEffect(() => {
    // Allow authenticated users to access signup if they're recovering a session
    // The recovery flow (SignUpRecover.tsx) validates that the authenticated user's
    // email matches the session email, so this is safe
    if (isAuthenticated && !recovered) {
      navigate('/login');
      return;
    }

    // Don't reset state if coming from recovery
    if (recovered) {
      setSearchParameters('');
      return;
    }

    // Defensive check: Clear stale signup state from localStorage if at final step
    // This prevents showing the confirmation page when starting a new signup
    try {
      const persistedSignUpState = localStorage.getItem('signUp-state');
      if (persistedSignUpState) {
        const parsed = JSON.parse(persistedSignUpState);
        const licenseCountInState = parsed?.licenseCount || 1;
        const finalStep = 4 + licenseCountInState * 2;
        if (parsed?.step === finalStep) {
          console.log(
            'Clearing stale sign-up state from localStorage (was at final step)',
          );
          localStorage.removeItem('signUp-state');
        }
      }
    } catch (error) {
      console.error('Error checking/clearing stale signup state:', error);
    }

    const firstStep = typeof skipAbout === 'string' ? 1 : 0;
    store.dispatch(resetSignUp(firstStep));
    if (program === 'ACT') {
      store.dispatch(setTrackGroup(TrackGroup.ACTSATPrep));
    }
    setSearchParameters('');
  }, []);

  // Validate session status if we have a sessionId
  const { data: sessionStatus, error: sessionStatusError } =
    useGetSessionStatusQuery(sessionId || '', {
      skip: !sessionId, // Only fetch if we have a sessionId
    });

  useEffect(() => {
    if (!sessionId) {
      return;
    }

    if (sessionStatusError) {
      // Session not found or expired
      console.log('Session not found or expired, clearing sign-up state');
      localStorage.removeItem('signUp-state');
      store.dispatch(resetSignUp(0));
      toast.error('Session expired. Please start over.', {
        id: 'session-invalid',
        duration: 7000,
      });
      // Stay on signup page (now at step 0) so user can start fresh
      return;
    }

    if (sessionStatus?.isCompleted) {
      // Session already completed - show success page
      console.log('Session already completed, showing success page');

      // Calculate the final step to show SubscriptionConfirm
      const finalStep = 4 + licenseCount * 2;
      store.dispatch(setStep(finalStep));

      // Clear localStorage to prevent stale sessionId on next visit
      localStorage.removeItem('signUp-state');

      // SubscriptionConfirm handles the redirect:
      // - For authenticated users: shows success page, then redirects to dashboard after 7s
      // - For non-authenticated users: shows success page with "Get Started" button
    }
  }, [sessionId, sessionStatus, sessionStatusError, licenseCount]);

  const renderStep = () => {
    const thinkerDetailSteps = licenseCount * 2; // Each thinker detail step followed by a track recommendation step

    if (step === 0) {
      return <About onGetStarted={handleNextStep} />;
    }
    if (step === 1) {
      return (
        <Payment
          onPreviousStep={handlePreviousStep}
          onNextStep={(paymentType) => {
            if (paymentType === 'trial') {
              toast.success(
                `Your 7-day trial has been activated! Now let's set up your account.`,
              );
            } else if (paymentType === 'discounted') {
              toast.success(
                `Subscription activated with your discount! Now let's set up your account.`,
              );
            } else {
              toast.success(
                `Successful payment! Now let's set up your account.`,
              );
            }
            handleNextStep();
          }}
        />
      );
    }
    if (step === 2) {
      return (
        <MentorDetails
          onPreviousStep={handlePreviousStep}
          onNextStep={handleNextStep}
        />
      );
    }
    if (step >= 3 && step < 3 + thinkerDetailSteps) {
      const thinkerSetupStep = step - 3;
      return thinkerSetupStep % 2 === 0 ? (
        <ThinkerDetails
          onNextStep={handleNextStep}
          onPreviousStep={handlePreviousStep}
          licenseIndex={Math.floor(thinkerSetupStep / 2)}
        />
      ) : (
        <TrackRecommendation
          onNextStep={handleNextStep}
          onPreviousStep={handlePreviousStep}
          licenseIndex={Math.floor(thinkerSetupStep / 2)}
        />
      );
    }
    if (step === 3 + thinkerDetailSteps) {
      return (
        <SignUpQuestionnaire
          onPreviousStep={handlePreviousStep}
          onNextStep={handleNextStep}
        />
      );
    }
    if (step === 4 + thinkerDetailSteps) {
      return <SubscriptionConfirm />;
    }
  };

  return (
    <div
      className={`
        ${
          step > 1 ? 'mt-8 md:mt-0' : ''
        } flex flex-col mx-auto w-full h-full md:w-[90%] items-center p-4
        ${
          previousIndex < step
            ? 'animate-slideInFromRight'
            : previousIndex > step
            ? 'animate-slideInFromLeft'
            : ''
        }
      `}
    >
      {renderStep()}
    </div>
  );
};

export default SignUp;
